import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  Users,
  Building2,
  Briefcase,
  FileText,
  ClipboardList,
  Video,
  Bot,
  Target,
  UserCheck,
  Workflow,
  Network,
  Calendar,
  CalendarDays,
  BarChart3,
  Shield,
  Sparkles,
  Sun,
  Wand2,
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Bell,
  Moon,
  Radio,
  LogOut,
  Filter,
  Plus,
  Download,
  SlidersHorizontal,
  RefreshCw,
  MoreHorizontal,
} from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { GGJLogoMark } from '@shared/components/Brand/GGJLogo';

/** Full product map — sidebar groups mirror the live B2B app (see marketing screenshots). */
type ViewId =
  | 'dashboard'
  | 'candidates'
  | 'clients'
  | 'jobs'
  | 'requisitions'
  | 'applications'
  | 'interviews'
  | 'aiInterviews'
  | 'precisionMatch'
  | 'bestCandidates'
  | 'agentHub'
  | 'workflows'
  | 'orchestrator'
  | 'scheduler'
  | 'calendarNotes'
  | 'reports'
  | 'rbac';

type NavItem = { id: ViewId; label: string; icon: LucideIcon; badge?: string };

const SECTIONS: { title: string; items: NavItem[] }[] = [
  {
    title: 'Overview',
    items: [{ id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    title: 'Data',
    items: [
      { id: 'candidates', label: 'Candidates', icon: Users },
      { id: 'clients', label: 'Clients', icon: Building2 },
      { id: 'jobs', label: 'Jobs', icon: Briefcase },
      { id: 'requisitions', label: 'Requisitions', icon: FileText },
    ],
  },
  {
    title: 'Pipeline',
    items: [
      { id: 'applications', label: 'Applications', icon: ClipboardList },
      { id: 'interviews', label: 'Interviews', icon: Video },
      { id: 'aiInterviews', label: 'AI interviews', icon: Sparkles, badge: 'Live' },
    ],
  },
  {
    title: 'RoleFit AI',
    items: [
      { id: 'precisionMatch', label: 'Precision match', icon: Target },
      { id: 'bestCandidates', label: 'Best candidates', icon: UserCheck },
    ],
  },
  {
    title: 'Agent & AI',
    items: [{ id: 'agentHub', label: 'Agent · agentic interview', icon: Wand2 }],
  },
  {
    title: 'Automations',
    items: [
      { id: 'workflows', label: 'Workflows', icon: Workflow },
      { id: 'orchestrator', label: 'Process Orchestrator', icon: Network },
    ],
  },
  {
    title: 'Planning',
    items: [
      { id: 'scheduler', label: 'Scheduler', icon: Calendar },
      { id: 'calendarNotes', label: 'Calendar & notes', icon: CalendarDays },
    ],
  },
  {
    title: 'Insights & access',
    items: [
      { id: 'reports', label: 'Reports', icon: BarChart3 },
      { id: 'rbac', label: 'Manage rights · RBAC', icon: Shield },
    ],
  },
];

const FLAT_VIEWS: NavItem[] = SECTIONS.flatMap((s) => s.items);

const NAV_DROPDOWN_GROUPS = [
  {
    title: 'Matching & AI',
    items: ['Single optimize', 'RoleFit AI', 'Role fit matrix', 'Bulk process'],
  },
  { title: 'Automations', items: ['Process Orchestrator', 'Workflows'] },
  { title: 'Planning', items: ['Scheduler', 'Calendar & notes'] },
  {
    title: 'Collaboration',
    items: ['My activities', 'Interview hub', 'AI interviews', 'Agentic interview'],
  },
];

const CYCLE_MS = 3800;

/** Scripted tour step — cursor moves to (cx, cy) % of container then fires a click */
type TourStep = {
  viewId: ViewId;
  /** If set, clicking shows this drill-down view instead of navigating away */
  drillId?: 'candidateDetail' | 'jobDetail';
  /** Cursor X position as % of showcase container width */
  cx: number;
  /** Cursor Y position as % of showcase container height */
  cy: number;
  /** How long (ms) to display the result before advancing */
  dwellMs: number;
};

/**
 * Curated tour: Dashboard → Candidates list → Candidate detail →
 * Jobs list → Job detail → AI Interviews → Agent → Precision Match → Reports
 */
const TOUR_SCRIPT: TourStep[] = [
  // 0 — Dashboard: cursor on billing card
  { viewId: 'dashboard', cx: 63, cy: 57, dwellMs: 2200 },
  // 1 — Sidebar: click Candidates nav item
  { viewId: 'candidates', cx: 13, cy: 34, dwellMs: 1200 },
  // 2 — Candidates table: click "Kiran Rao" row → detail
  { viewId: 'candidates', drillId: 'candidateDetail', cx: 56, cy: 49, dwellMs: 2800 },
  // 3 — Sidebar: click Jobs nav item
  { viewId: 'jobs', cx: 13, cy: 42, dwellMs: 1200 },
  // 4 — Jobs table: click "Staff Engineer" row → detail
  { viewId: 'jobs', drillId: 'jobDetail', cx: 56, cy: 42, dwellMs: 2600 },
  // 5 — Sidebar: click AI Interviews
  { viewId: 'aiInterviews', cx: 13, cy: 64, dwellMs: 2000 },
  // 6 — Topbar: click Agent button
  { viewId: 'agentHub', cx: 74, cy: 10, dwellMs: 2000 },
  // 7 — Sidebar: click Precision match
  { viewId: 'precisionMatch', cx: 13, cy: 74, dwellMs: 2200 },
  // 8 — Sidebar: click Reports
  { viewId: 'reports', cx: 13, cy: 85, dwellMs: 2000 },
];

function breadcrumbFor(id: ViewId): string {
  const map: Partial<Record<ViewId, string>> = {
    dashboard: 'Overview > Dashboard',
    candidates: 'Data > Candidates',
    clients: 'Data > Clients',
    jobs: 'Data > Jobs',
    requisitions: 'Data > Requisitions',
    applications: 'Pipeline > Applications',
    interviews: 'Pipeline > Interviews',
    aiInterviews: 'Pipeline > AI interviews',
    precisionMatch: 'RoleFit AI > Precision match',
    bestCandidates: 'RoleFit AI > Best candidates',
    agentHub: 'Agent > Agentic interview',
    workflows: 'Automations > Workflows',
    orchestrator: 'Automations > Process Orchestrator',
    scheduler: 'Planning > Scheduler',
    calendarNotes: 'Planning > Calendar & notes',
    reports: 'Insights > Reports',
    rbac: 'Settings > Manage rights',
  };
  return map[id] ?? 'GoGrabJob Business';
}

/**
 * Light palette from B2C `public/mobile/light.html` (--primary #8E86FF, --bg-secondary #f3f3f3).
 * Dark mode keeps zinc/violet Tailwind tokens.
 */
const sim = {
  page: 'text-[#000000] dark:text-zinc-50',
  muted: 'text-[#616161] dark:text-zinc-400',
  subtle: 'text-[#8c8c8c] dark:text-zinc-500',
  border: 'border-[#e5e5e5] dark:border-zinc-700',
  surface: 'bg-[#ffffff] dark:bg-zinc-950',
  chrome: 'bg-[#f3f3f3] dark:bg-zinc-900/90',
  accent: 'text-[#8E86FF] dark:text-violet-300',
  accentBg: 'bg-[#8E86FF] dark:bg-violet-500',
  accentSoft: 'bg-[#f0eeff] dark:bg-violet-950/45',
  accentBorder: 'border-[rgba(142,134,255,0.35)] dark:border-violet-800',
  accentStrong: 'text-[#7B6EE8] dark:text-violet-200',
  rowHover: 'hover:bg-[rgba(142,134,255,0.08)] dark:hover:bg-zinc-800/55',
} as const;

function TableShell({ children }: { children: ReactNode }) {
  return (
    <div className={`rounded-lg ${sim.border} ${sim.surface} shadow-sm overflow-hidden`}>{children}</div>
  );
}

function DataTableToolbar({ left, right }: { left: ReactNode; right?: ReactNode }) {
  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 justify-between border-b ${sim.border} ${sim.chrome} px-2 py-1.5`}
    >
      <div className="flex flex-wrap items-center gap-1 min-w-0">{left}</div>
      {right && <div className="flex flex-wrap items-center gap-1 shrink-0 justify-end">{right}</div>}
    </div>
  );
}

function TbBtn({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-md border ${sim.border} ${sim.surface} px-1.5 py-0.5 text-[9px] font-medium text-[#616161] shadow-sm dark:text-zinc-200 ${className}`}
    >
      {children}
    </span>
  );
}

function TbIconBtn({ children }: { children: ReactNode }) {
  return (
    <span
      className={`inline-flex h-6 w-6 items-center justify-center rounded-md border ${sim.border} ${sim.surface} text-[#8c8c8c] dark:text-zinc-400`}
    >
      {children}
    </span>
  );
}

function DataTablePagination({
  from,
  to,
  total,
  pageSize,
}: {
  from: number;
  to: number;
  total: number;
  pageSize: number;
}) {
  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-2 border-t ${sim.border} ${sim.surface} px-2 py-1.5 text-[9px] ${sim.muted}`}
    >
      <span>
        Showing{' '}
        <span className="font-semibold text-[#000000] dark:text-zinc-200">
          {from}–{to}
        </span>{' '}
        of <span className="font-semibold text-[#616161] dark:text-zinc-200">{total}</span>
      </span>
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-1">
          Rows
          <span className={`rounded border ${sim.border} ${sim.chrome} px-1.5 py-0.5 font-medium text-zinc-700`}>
            {pageSize}
          </span>
        </span>
        <div className={`flex overflow-hidden rounded border ${sim.border}`}>
          <button
            type="button"
            className={`px-2 py-0.5 ${sim.chrome} text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800`}
          >
            Prev
          </button>
          <button
            type="button"
            className={`border-l ${sim.border} px-2 py-0.5 ${sim.chrome} text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

function PageHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="mb-2 space-y-0.5">
      <h3 className={`text-sm font-bold ${sim.page}`}>{title}</h3>
      <p className={`text-[10px] leading-snug ${sim.muted}`}>{subtitle}</p>
    </div>
  );
}

/* ─── Panels — dense “real app” simulations ───────────────────────────── */

function PanelDashboardFull() {
  const metrics = [
    { k: 'Open Roles', v: '14' },
    { k: 'New Applications (30D)', v: '14' },
    { k: 'Pipeline (all stages)', v: '29' },
    { k: 'Open Requisitions', v: '17' },
    { k: 'New Applications (today · 7D)', v: '0 · 0' },
    { k: 'Interviews today', v: '0' },
  ];
  const months = ['Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr'];
  const linePts = [42, 38, 55, 48, 72, 68];
  const linePath = linePts
    .map((v, i) => {
      const x = (i / (linePts.length - 1)) * 100;
      const y = 100 - v;
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');
  return (
    <div className="space-y-2">
      <PageHeader
        title="Dashboard"
        subtitle="Pipeline health, billing, and team activity for your organisation."
      />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
        {metrics.map((m) => (
          <div
            key={m.k}
            className={`rounded-lg border ${sim.border} ${sim.surface} px-2 py-1.5 shadow-sm`}
          >
            <p className={`text-[8px] font-semibold uppercase tracking-wide ${sim.muted}`}>{m.k}</p>
            <p className={`text-lg font-bold ${sim.page} leading-tight`}>{m.v}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-1.5">
        <div className={`rounded-lg border ${sim.border} p-2 ${sim.surface} shadow-sm`}>
          <p className="text-[10px] font-semibold text-[#000000] dark:text-zinc-100">Revenue & billing pulse</p>
          <p className={`text-[8px] ${sim.subtle} mb-1`}>Trailing 6 months</p>
          <div className="relative h-24 w-full">
            <svg viewBox="0 0 100 100" className="w-full h-full overflow-visible" preserveAspectRatio="none">
              <defs>
                <linearGradient id="heroLineFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.18" />
                  <stop offset="100%" stopColor="#7c3aed" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d={`${linePath} L 100 100 L 0 100 Z`}
                fill="url(#heroLineFill)"
                vectorEffect="non-scaling-stroke"
              />
              <path d={linePath} fill="none" stroke="#7c3aed" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
            </svg>
            <div className="flex justify-between text-[8px] text-zinc-400 -mt-0.5 px-0.5">
              {months.map((m) => (
                <span key={m}>{m}</span>
              ))}
            </div>
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {['Billing settings', 'Revenue detail', 'Applications'].map((x) => (
              <span
                key={x}
                className={`text-[8px] rounded border ${sim.accentBorder} ${sim.accentSoft} px-1.5 py-0.5 font-medium text-[#7B6EE8] dark:text-violet-200`}
              >
                {x}
              </span>
            ))}
          </div>
        </div>
        <div className={`flex flex-col rounded-lg border ${sim.border} ${sim.surface} shadow-sm`}>
          <div className={`flex items-center justify-between gap-2 border-b ${sim.border} ${sim.chrome} px-2 py-1`}>
            <div>
              <p className="text-[10px] font-semibold text-[#000000] dark:text-zinc-100">Saved reports</p>
              <p className={`text-[8px] ${sim.subtle}`}>Scheduled exports · email</p>
            </div>
            <div className="flex gap-1">
              <TbBtn>
                <RefreshCw className="h-3 w-3" /> Sync
              </TbBtn>
              <TbBtn>
                <Plus className="h-3 w-3" /> New
              </TbBtn>
            </div>
          </div>
          <div className="text-[9px]">
            <div
              className={`grid grid-cols-12 gap-1 border-b ${sim.border} ${sim.chrome} px-2 py-1 font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400`}
            >
              <span className="col-span-5">Name</span>
              <span className="col-span-3">Cadence</span>
              <span className="col-span-2">Last run</span>
              <span className="col-span-2">Status</span>
            </div>
            {[
              ['Pipeline funnel', 'Weekly Mon', 'Apr 7', 'OK'],
              ['Placement MTD', 'Daily 08:00', 'Apr 9', 'OK'],
              ['Client SLA', 'Weekly Fri', 'Apr 5', 'Queued'],
            ].map(([a, b, c, d]) => (
              <div key={a} className={`grid grid-cols-12 gap-1 border-b ${sim.border} px-2 py-1 text-zinc-700`}>
                <span className="col-span-5 truncate font-medium text-zinc-900">{a}</span>
                <span className="col-span-3 truncate">{b}</span>
                <span className="col-span-2">{c}</span>
                <span className="col-span-2 text-emerald-600">{d}</span>
              </div>
            ))}
          </div>
          <DataTablePagination from={1} to={3} total={12} pageSize={10} />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-1.5">
        <div className={`rounded-lg border ${sim.border} p-2 ${sim.surface} shadow-sm`}>
          <p className="text-[10px] font-semibold text-[#000000] dark:text-zinc-100">Business pulse</p>
          <p className={`text-[8px] ${sim.subtle} mb-1`}>Monthly activity</p>
          <div className="flex h-24 items-end gap-1 px-0.5">
            {[
              { m: 'Jan', h: 22 },
              { m: 'Feb', h: 32 },
              { m: 'Mar', h: 88 },
              { m: 'Apr', h: 64 },
            ].map((b) => (
              <div key={b.m} className="flex min-w-0 flex-1 flex-col items-center gap-0.5">
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${b.h}%` }}
                  className="w-full max-w-[36px] rounded-t bg-[#8E86FF] dark:bg-violet-500"
                  style={{ minHeight: 12 }}
                />
                <span className="text-[8px] text-zinc-500">{b.m}</span>
              </div>
            ))}
          </div>
        </div>
        <div className={`rounded-lg border ${sim.border} p-2 ${sim.surface} shadow-sm`}>
          <p className="text-[10px] font-semibold text-[#000000] dark:text-zinc-100">Placement momentum</p>
          <p className={`text-[8px] ${sim.subtle} mb-1`}>Count per application status</p>
          <div className="flex h-24 items-end justify-between gap-0.5 px-0.5">
            {['Sub', 'View', 'Short', 'Int', 'Off', 'Acc', 'Rej', 'Pl'].map((label, i) => (
              <div key={label} className="flex min-w-0 max-w-[12%] flex-1 flex-col items-center">
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${[30, 45, 60, 55, 40, 25, 15, 50][i]}%` }}
                  className="w-full rounded-t bg-[#8E86FF] dark:bg-violet-500"
                  style={{ minHeight: 8 }}
                />
                <span className="mt-0.5 text-center text-[7px] leading-none text-zinc-400">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-1.5">
        <div className={`rounded-lg border ${sim.border} p-2 ${sim.surface} shadow-sm`}>
          <p className="text-[10px] font-semibold text-[#000000] dark:text-zinc-100">Pool vs backlog</p>
          <p className={`text-[8px] ${sim.subtle} mb-1`}>Backlog: Submitted + viewed</p>
          <div className="flex h-16 items-end gap-2">
            <div className="flex flex-1 flex-col items-center">
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: '85%' }}
                className="w-full max-w-[48px] rounded-t bg-[#8E86FF] dark:bg-violet-500"
              />
              <span className="mt-0.5 text-[8px] font-medium text-zinc-600">Pool</span>
            </div>
            <div className="flex flex-1 flex-col items-center">
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: '38%' }}
                className="w-full max-w-[48px] rounded-t bg-zinc-300"
              />
              <span className="mt-0.5 text-[8px] font-medium text-zinc-600">Backlog</span>
            </div>
          </div>
          <div className="mt-1 flex gap-1">
            <TbBtn>Candidates</TbBtn>
            <TbBtn>Applications</TbBtn>
          </div>
        </div>
        <div className={`rounded-lg border ${sim.border} p-2 ${sim.surface} shadow-sm`}>
          <div className="mb-1 flex items-center justify-between">
            <p className="text-[10px] font-semibold text-[#000000] dark:text-zinc-100">Team status</p>
            <span className={`rounded border ${sim.border} ${sim.chrome} px-1.5 py-0.5 text-[8px] text-zinc-500`}>
              This week
            </span>
          </div>
          <div className={`flex h-16 items-end justify-center gap-1 rounded border ${sim.border} ${sim.chrome} py-2`}>
            {[40, 55, 35].map((h, i) => (
              <div key={i} className="w-3 rounded-t bg-[#8E86FF]/45 dark:bg-violet-300" style={{ height: `${h}%` }} />
            ))}
          </div>
          <div className="mt-1 flex justify-center gap-1">
            <TbBtn>Team roster</TbBtn>
            <TbBtn>Applications</TbBtn>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        {['Reports', 'Applications', 'Interviews', 'Activity feed'].map((x) => (
          <button
            key={x}
            type="button"
            className={`rounded-lg px-2.5 py-1.5 text-[9px] font-semibold text-white shadow-sm ${sim.accentBg} hover:bg-[#7B6EE8]`}
          >
            {x}
          </button>
        ))}
      </div>
    </div>
  );
}

function PanelCandidatesFull() {
  const rows = [
    ['Meenakshi P', 'meenakshi.p@gmail.com', 'UI/UX Designer', 'Capgemini', 'Pune', 'Figma', 'Open', 'Uploaded'],
    ['Sunil Batra', 'sunil.b@mp.com', 'Senior Java Dev', 'Mphasis', 'Bengaluru', 'Java · Spring', 'Open', 'None'],
    ['Kiran Rao', 'kiran.r@infy.com', 'Tech Lead', 'Infosys', 'Chennai', 'React · Node', 'Review', 'Uploaded'],
    ['Ananya D', 'ananya.d@gmail.com', 'PM', 'Wipro', 'Hyderabad', 'Agile', 'Open', 'Uploaded'],
    ['Vikram S', 'vik.s@tcs.com', 'Data Eng', 'TCS', 'Mumbai', 'Spark · SQL', 'Open', 'None'],
    ['Neha K', 'neha.k@lt.com', 'TA Partner', 'L&T', 'Pune', 'Sourcing', 'Hold', 'Uploaded'],
  ];
  return (
    <div className="space-y-1.5">
      <PageHeader
        title="Candidates"
        subtitle="20 candidates in your pool · $9.5 intake wizard on New candidate"
      />
      <TableShell>
        <DataTableToolbar
          left={
            <>
              <TbBtn>
                <Filter className="h-3 w-3" /> Filters
              </TbBtn>
              <TbBtn>
                <SlidersHorizontal className="h-3 w-3" /> Columns
              </TbBtn>
              <TbBtn>
                <Search className="h-3 w-3" /> Find in table
              </TbBtn>
            </>
          }
          right={
            <>
              <TbBtn>
                <Download className="h-3 w-3" /> Export
              </TbBtn>
              <TbBtn>
                <Download className="h-3 w-3" /> Import
              </TbBtn>
              <TbBtn>Delete</TbBtn>
              <TbBtn>Edit</TbBtn>
              <span
                className={`inline-flex items-center gap-0.5 rounded-md border ${sim.accentBorder} ${sim.accentSoft} px-1.5 py-0.5 text-[9px] font-semibold text-[#7B6EE8] dark:text-violet-200`}
              >
                Manage rights
              </span>
              <span className={`inline-flex items-center gap-0.5 rounded-md ${sim.accentBg} px-2 py-0.5 text-[9px] font-semibold text-white`}>
                <Plus className="h-3 w-3" /> New candidate
              </span>
              <TbIconBtn>
                <MoreHorizontal className="h-3.5 w-3.5" />
              </TbIconBtn>
            </>
          }
        />
        <div className="overflow-x-auto text-[9px]">
          <div
            className={`grid min-w-[640px] grid-cols-[28px_1.1fr_1.3fr_1fr_0.9fr_0.7fr_0.6fr_1fr_0.5fr_0.7fr_1fr] gap-1 border-b ${sim.border} ${sim.chrome} px-2 py-1 font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400`}
          >
            <span className="flex justify-center">☐</span>
            <span>Name</span>
            <span>Email</span>
            <span>Current title</span>
            <span>Company</span>
            <span>Loc.</span>
            <span>Src.</span>
            <span>Skills</span>
            <span>Fit</span>
            <span>Res.</span>
            <span>Created</span>
          </div>
          {rows.map((row) => (
            <div
              key={row[0]}
              className={`grid min-w-[640px] grid-cols-[28px_1.1fr_1.3fr_1fr_0.9fr_0.7fr_0.6fr_1fr_0.5fr_0.7fr_1fr] gap-1 border-b ${sim.border} px-2 py-1 text-[#000000] dark:text-zinc-100 ${sim.rowHover}`}
            >
              <span className="flex justify-center text-zinc-400">☐</span>
              <span className="truncate font-medium text-zinc-900">{row[0]}</span>
              <span className="truncate text-[#8E86FF] dark:text-violet-300">{row[1]}</span>
              <span className="truncate">{row[2]}</span>
              <span className="truncate">{row[3]}</span>
              <span className="truncate text-zinc-600">{row[4]}</span>
              <span className="truncate text-zinc-400">—</span>
              <span className="truncate text-zinc-600">{row[5]}</span>
              <span className="text-zinc-600">{row[6]}</span>
              <span className={row[7] === 'Uploaded' ? 'text-emerald-600' : 'text-zinc-400'}>{row[7]}</span>
              <span className="truncate text-zinc-500">2026-04-0{rows.indexOf(row) + 1} 13:29</span>
            </div>
          ))}
        </div>
        <DataTablePagination from={1} to={6} total={20} pageSize={10} />
      </TableShell>
    </div>
  );
}

/** Drill-down: candidate profile opened from candidates table row */
function PanelCandidateDetail() {
  return (
    <div className="space-y-2">
      {/* Back breadcrumb */}
      <div className="flex items-center gap-1.5 text-[9px] text-zinc-400 dark:text-zinc-500">
        <span className={`cursor-pointer ${sim.accent} font-semibold`}>Candidates</span>
        <ChevronRight className="h-3 w-3" />
        <span className="font-medium text-zinc-600 dark:text-zinc-300">Kiran Rao</span>
      </div>

      {/* Header card */}
      <div className={`flex items-start gap-3 rounded-xl border ${sim.border} ${sim.surface} p-2.5 shadow-sm`}>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#8E86FF] to-[#7B6EE8] text-sm font-bold text-white shadow">
          KR
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-extrabold text-zinc-900 dark:text-zinc-50">Kiran Rao</h3>
            <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[8px] font-semibold text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
              Active
            </span>
          </div>
          <p className="text-[10px] text-zinc-500 dark:text-zinc-400">Tech Lead · Infosys · Chennai</p>
          <p className={`text-[10px] font-medium ${sim.accent}`}>kiran.r@infy.com</p>
        </div>
        <div className={`flex shrink-0 flex-col items-center rounded-lg border ${sim.accentBorder} ${sim.accentSoft} px-2.5 py-1.5`}>
          <span className="text-[7px] font-bold uppercase tracking-wide text-zinc-500">RoleFit</span>
          <span className="text-xl font-black text-[#7B6EE8] dark:text-violet-300">84</span>
        </div>
      </div>

      {/* Skills + Timeline */}
      <div className="grid grid-cols-2 gap-1.5">
        <div className={`rounded-lg border ${sim.border} ${sim.surface} p-2 shadow-sm`}>
          <p className="mb-1.5 text-[8px] font-bold uppercase tracking-wide text-zinc-500">Skills</p>
          <div className="flex flex-wrap gap-1">
            {['React', 'Node.js', 'TypeScript', 'AWS', 'System Design'].map((s) => (
              <span key={s} className={`rounded border ${sim.accentBorder} ${sim.accentSoft} px-1.5 py-0.5 text-[8px] font-medium text-[#7B6EE8] dark:text-violet-200`}>
                {s}
              </span>
            ))}
          </div>
        </div>
        <div className={`rounded-lg border ${sim.border} ${sim.surface} p-2 shadow-sm`}>
          <p className="mb-1.5 text-[8px] font-bold uppercase tracking-wide text-zinc-500">Timeline</p>
          {[
            ['Applied', '2026-04-03'],
            ['Shortlisted', '2026-04-05'],
            ['AI Interview', 'Scheduled'],
          ].map(([event, date]) => (
            <div key={event} className="mb-0.5 flex items-center justify-between text-[9px]">
              <span className="text-zinc-700 dark:text-zinc-200">{event}</span>
              <span className="text-zinc-400">{date}</span>
            </div>
          ))}
        </div>
      </div>

      {/* AI Interview score bars */}
      <div className={`rounded-lg border ${sim.accentBorder} ${sim.accentSoft} p-2`}>
        <p className="mb-1.5 text-[8px] font-bold uppercase tracking-wide text-zinc-500 dark:text-violet-200">AI Interview scores</p>
        {[
          { label: 'Technical depth', score: 82 },
          { label: 'Communication', score: 91 },
          { label: 'Problem solving', score: 78 },
          { label: 'Culture fit', score: 85 },
        ].map(({ label, score }) => (
          <div key={label} className="mb-1 space-y-0.5">
            <div className="flex justify-between text-[9px]">
              <span className="font-medium text-zinc-700 dark:text-zinc-200">{label}</span>
              <span className="font-bold text-[#8E86FF] dark:text-violet-300">{score}%</span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-white/60 dark:bg-zinc-700">
              <motion.div
                className="h-full rounded-full bg-[#8E86FF] dark:bg-violet-500"
                initial={{ width: 0 }}
                animate={{ width: `${score}%` }}
                transition={{ duration: 0.6 }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Action row */}
      <div className="flex flex-wrap gap-1">
        {['Shortlist', 'Schedule AI Interview', 'View résumé', 'Add note'].map((a) => (
          <button
            key={a}
            type="button"
            className={`rounded-lg px-2 py-1 text-[9px] font-semibold shadow-sm ${
              a === 'Shortlist'
                ? `text-white ${sim.accentBg}`
                : `border ${sim.border} ${sim.surface} text-zinc-700 dark:text-zinc-200`
            }`}
          >
            {a}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Drill-down: job detail opened from jobs table row */
function PanelJobDetail() {
  return (
    <div className="space-y-2">
      {/* Back breadcrumb */}
      <div className="flex items-center gap-1.5 text-[9px] text-zinc-400 dark:text-zinc-500">
        <span className={`cursor-pointer ${sim.accent} font-semibold`}>Jobs</span>
        <ChevronRight className="h-3 w-3" />
        <span className="font-medium text-zinc-600 dark:text-zinc-300">Staff Engineer</span>
      </div>

      {/* Header */}
      <div className={`rounded-xl border ${sim.border} ${sim.surface} p-2.5 shadow-sm`}>
        <div className="mb-1 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-extrabold text-zinc-900 dark:text-zinc-50">Staff Engineer</h3>
            <p className="text-[10px] text-zinc-500 dark:text-zinc-400">Acme Bank · Hybrid · Mumbai</p>
          </div>
          <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[8px] font-semibold text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
            Active
          </span>
        </div>
        <div className="flex flex-wrap gap-1">
          {['Java · Spring', 'AWS', 'Microservices', 'Fintech'].map((t) => (
            <span key={t} className={`rounded border ${sim.border} ${sim.chrome} px-1.5 py-0.5 text-[8px] text-zinc-600 dark:text-zinc-300`}>
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* Pipeline metrics */}
      <div className="grid grid-cols-3 gap-1.5">
        {[['Applications', '24'], ['Shortlisted', '8'], ['Interviews', '5']].map(([k, v]) => (
          <div key={k} className={`rounded-lg border ${sim.border} ${sim.surface} px-2 py-1.5 text-center shadow-sm`}>
            <p className="text-[8px] font-semibold uppercase text-zinc-500">{k}</p>
            <p className="text-base font-bold text-zinc-900 dark:text-zinc-50">{v}</p>
          </div>
        ))}
      </div>

      {/* Top candidates table */}
      <TableShell>
        <div className={`border-b ${sim.border} ${sim.chrome} px-2 py-1 text-[8px] font-bold uppercase tracking-wide text-zinc-500`}>
          Top candidates · RoleFit ranked
        </div>
        <div className="text-[9px]">
          {[
            ['Priya S.', 'Staff Eng · Acme', '87', 'Interview'],
            ['Amit K.', 'Staff Eng · Acme', '81', 'Screen'],
            ['Neha R.', 'Designer · Globex', '90', 'Offer'],
          ].map(([name, role, score, stage]) => (
            <div key={name} className={`grid grid-cols-12 gap-1 border-b ${sim.border} px-2 py-1 text-zinc-700 dark:text-zinc-200 ${sim.rowHover}`}>
              <span className="col-span-3 font-semibold text-zinc-900 dark:text-zinc-50">{name}</span>
              <span className="col-span-4 truncate text-zinc-500">{role}</span>
              <span className={`col-span-2 font-bold ${sim.accent}`}>{score}</span>
              <span className="col-span-3 truncate">{stage}</span>
            </div>
          ))}
        </div>
        <DataTablePagination from={1} to={3} total={24} pageSize={10} />
      </TableShell>

      {/* Actions */}
      <div className="flex flex-wrap gap-1">
        {['View pipeline', 'Run AI interviews', 'Export brief', 'Edit job'].map((a) => (
          <button
            key={a}
            type="button"
            className={`rounded-lg px-2 py-1 text-[9px] font-semibold shadow-sm ${
              a === 'View pipeline'
                ? `text-white ${sim.accentBg}`
                : `border ${sim.border} ${sim.surface} text-zinc-700 dark:text-zinc-200`
            }`}
          >
            {a}
          </button>
        ))}
      </div>
    </div>
  );
}

function CriteriaBar({ label, weight, fill }: { label: string; weight: number; fill: number }) {
  return (
    <div className="space-y-0.5">
      <div className="flex justify-between text-[9px]">
        <span className="font-medium text-zinc-700 dark:text-zinc-200">{label}</span>
        <span className="font-bold text-[#8E86FF] dark:text-violet-300">{weight}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-[#f3f3f3] dark:bg-zinc-800">
        <motion.div
          className="h-full rounded-full bg-[#8E86FF] dark:bg-violet-500"
          initial={{ width: 0 }}
          animate={{ width: `${fill}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
    </div>
  );
}

function PanelPrecisionMatch() {
  const criteria = [
    { label: 'Cloud architecture', weight: 25, fill: 100 },
    { label: 'Stakeholder communication', weight: 20, fill: 100 },
    { label: 'Java / Spring ecosystem', weight: 30, fill: 100 },
    { label: 'Team leadership', weight: 15, fill: 100 },
    { label: 'Domain — fintech', weight: 10, fill: 100 },
  ];
  return (
    <div className="space-y-1.5">
      <PageHeader
        title="Precision match"
        subtitle="Weighted JD criteria · explainable gaps for submittals · matrix & bulk compare"
      />
      <div className="grid gap-1.5 lg:grid-cols-2">
        <div className={`space-y-2 rounded-lg border ${sim.accentBorder} ${sim.accentSoft} p-2`}>
          <p className="text-[9px] font-bold uppercase tracking-wide text-[#000000] dark:text-violet-200">JD weights</p>
          {criteria.map((c) => (
            <CriteriaBar key={c.label} label={c.label} weight={c.weight} fill={c.fill} />
          ))}
        </div>
        <div className={`space-y-2 rounded-lg border ${sim.border} ${sim.surface} p-2 shadow-sm`}>
          <div className="flex items-start justify-between gap-2">
            <p className="text-[9px] font-bold uppercase tracking-wide text-zinc-500">Candidate · Priya S.</p>
            <span className="rounded border border-[rgba(142,134,255,0.35)] bg-white px-1.5 py-0.5 text-[9px] font-black text-[#7B6EE8] dark:border-violet-800 dark:bg-zinc-900 dark:text-violet-200">
              87
            </span>
          </div>
          <p className="text-[9px] text-zinc-600 dark:text-zinc-300">
            Strong on <span className="font-semibold text-zinc-900 dark:text-zinc-50">Java & leadership</span>
          </p>
          <p className="inline-block rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[9px] text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
            Gap: regulated cloud exposure — probe in AI interview
          </p>
          <TableShell>
            <div
              className={`grid grid-cols-12 gap-1 border-b ${sim.border} ${sim.chrome} px-2 py-1 text-[9px] font-semibold ${sim.muted}`}
            >
              <span className="col-span-5">Criterion</span>
              <span className="col-span-4">Signal</span>
              <span className="col-span-3 text-right">Score</span>
            </div>
            {[
              ['Java / Spring', 'Strong', '92%'],
              ['Cloud (AWS)', 'Partial', '61%'],
              ['Stakeholder mgmt', 'Strong', '88%'],
              ['Security review', 'Adequate', '74%'],
            ].map(([a, b, s]) => (
              <div key={a} className={`grid grid-cols-12 gap-1 border-b ${sim.border} px-2 py-1 text-[9px]`}>
                <span className="col-span-5 text-[#000000] dark:text-zinc-100">{a}</span>
                <span className="col-span-4 text-zinc-500">{b}</span>
                <span className="col-span-3 text-right font-bold text-[#8E86FF] dark:text-violet-300">{s}</span>
              </div>
            ))}
            <DataTablePagination from={1} to={4} total={18} pageSize={10} />
          </TableShell>
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        {['Open matrix', 'Bulk compare', 'Export brief'].map((x) => (
          <button
            key={x}
            type="button"
            className={`rounded-lg px-2 py-1 text-[9px] font-semibold text-white shadow-sm ${sim.accentBg} hover:bg-[#7B6EE8] dark:hover:bg-violet-600`}
          >
            {x}
          </button>
        ))}
      </div>
    </div>
  );
}

function PanelClients() {
  const rows = [
    ['Acme Bank', 'FS', 'Mumbai', '4', '₹ Active', '2.4M'],
    ['Northwind Tech', 'SaaS', 'Bengaluru', '2', 'Trial', '—'],
    ['Globex Retail', 'Retail', 'Pune', '6', '₹ Active', '890K'],
    ['Contoso', 'IT', 'Chennai', '1', 'Hold', '—'],
  ];
  return (
    <div className="space-y-1.5">
      <PageHeader title="Clients" subtitle="Accounts, contacts, commercials — jobs & requisitions roll up" />
      <TableShell>
        <DataTableToolbar
          left={
            <>
              <TbBtn>
                <Filter className="h-3 w-3" /> Segment
              </TbBtn>
              <TbBtn>
                <Search className="h-3 w-3" /> Search
              </TbBtn>
            </>
          }
          right={
            <>
              <TbBtn>
                <Download className="h-3 w-3" /> Export
              </TbBtn>
              <span className={`inline-flex items-center gap-0.5 rounded-md ${sim.accentBg} px-2 py-0.5 text-[9px] font-semibold text-white`}>
                <Plus className="h-3 w-3" /> New client
              </span>
            </>
          }
        />
        <div className="overflow-x-auto text-[9px]">
          <div
            className={`grid min-w-[560px] grid-cols-12 gap-1 border-b ${sim.border} ${sim.chrome} px-2 py-1 font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400`}
          >
            <span className="col-span-3">Account</span>
            <span className="col-span-2">Industry</span>
            <span className="col-span-2">Hub</span>
            <span className="col-span-2">Open reqs</span>
            <span className="col-span-2">Commercial</span>
            <span className="col-span-1 text-right">ARR</span>
          </div>
          {rows.map((r) => (
            <div
              key={r[0]}
              className={`grid min-w-[560px] grid-cols-12 gap-1 border-b ${sim.border} px-2 py-1 text-[#000000] dark:text-zinc-100 ${sim.rowHover}`}
            >
              <span className="col-span-3 truncate font-medium text-zinc-900">{r[0]}</span>
              <span className="col-span-2">{r[1]}</span>
              <span className="col-span-2">{r[2]}</span>
              <span className="col-span-2 font-semibold text-[#8E86FF] dark:text-violet-300">{r[3]}</span>
              <span className="col-span-2">{r[4]}</span>
              <span className="col-span-1 text-right text-zinc-500">{r[5]}</span>
            </div>
          ))}
        </div>
        <DataTablePagination from={1} to={4} total={28} pageSize={10} />
      </TableShell>
    </div>
  );
}

function PanelJobs() {
  const rows = [
    ['Staff Engineer', 'Acme Bank', 'Hybrid · Mumbai', '12', '12', 'Open'],
    ['Product Designer', 'Northwind', 'Remote', '8', '8', 'Open'],
    ['TA Lead', 'Globex', 'Pune', '5', '5', 'Hold'],
    ['SRE', 'Contoso', 'Bengaluru', '3', '3', 'Open'],
    ['Analyst', 'Acme Bank', 'Mumbai', '6', '4', 'Open'],
  ];
  return (
    <div className="space-y-1.5">
      <PageHeader title="Jobs" subtitle="Open roles · career site · linked requisitions & pipeline" />
      <TableShell>
        <DataTableToolbar
          left={
            <>
              <TbBtn>
                <Filter className="h-3 w-3" /> Status
              </TbBtn>
              <TbBtn>
                <SlidersHorizontal className="h-3 w-3" /> Columns
              </TbBtn>
            </>
          }
          right={
            <>
              <TbBtn>
                <Download className="h-3 w-3" /> Export
              </TbBtn>
              <span className={`inline-flex items-center gap-0.5 rounded-md ${sim.accentBg} px-2 py-0.5 text-[9px] font-semibold text-white`}>
                <Plus className="h-3 w-3" /> Publish
              </span>
            </>
          }
        />
        <div className="overflow-x-auto text-[9px]">
          <div
            className={`grid min-w-[560px] grid-cols-12 gap-1 border-b ${sim.border} ${sim.chrome} px-2 py-1 font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400`}
          >
            <span className="col-span-3">Title</span>
            <span className="col-span-2">Client</span>
            <span className="col-span-2">Location</span>
            <span className="col-span-2">Pipeline</span>
            <span className="col-span-2">Apps</span>
            <span className="col-span-1">State</span>
          </div>
          {rows.map((row) => (
            <div
              key={row[0]}
              className={`grid min-w-[560px] grid-cols-12 gap-1 border-b ${sim.border} px-2 py-1 ${sim.rowHover}`}
            >
              <span className="col-span-3 truncate font-semibold text-zinc-900">{row[0]}</span>
              <span className="col-span-2 truncate">{row[1]}</span>
              <span className="col-span-2 truncate text-zinc-600">{row[2]}</span>
              <span className="col-span-2 font-bold text-[#8E86FF] dark:text-violet-300">{row[3]}</span>
              <span className="col-span-2">{row[4]}</span>
              <span className="col-span-1 text-emerald-600">{row[5]}</span>
            </div>
          ))}
        </div>
        <DataTablePagination from={1} to={5} total={41} pageSize={10} />
      </TableShell>
    </div>
  );
}

function PanelRequisitions() {
  const rows = [
    ['REQ-2044', 'Staff Engineer · Acme', 'Approved', 'Sourcing', 'Apr 2', 'Neha'],
    ['REQ-2045', 'PM · Northwind', 'Pending', 'Intake', 'Apr 8', 'Ops'],
    ['REQ-2039', 'Designer · Globex', 'Approved', 'Offer', 'Mar 28', 'Neha'],
    ['REQ-2031', 'SRE · Contoso', 'Approved', 'On hold', 'Mar 12', 'Finance'],
  ];
  return (
    <div className="space-y-1.5">
      <PageHeader title="Requisitions" subtitle="Intake → approval → sourcing · client-linked hiring requests" />
      <TableShell>
        <DataTableToolbar
          left={
            <>
              <TbBtn>
                <Filter className="h-3 w-3" /> Stage
              </TbBtn>
              <TbBtn>
                <RefreshCw className="h-3 w-3" /> Sync
              </TbBtn>
            </>
          }
          right={
            <TbBtn>
              <Download className="h-3 w-3" /> Export
            </TbBtn>
          }
        />
        <div className="overflow-x-auto text-[9px]">
          <div
            className={`grid min-w-[580px] grid-cols-12 gap-1 border-b ${sim.border} ${sim.chrome} px-2 py-1 font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400`}
          >
            <span className="col-span-2">ID</span>
            <span className="col-span-3">Role</span>
            <span className="col-span-2">Approval</span>
            <span className="col-span-2">Stage</span>
            <span className="col-span-2">Updated</span>
            <span className="col-span-1">Owner</span>
          </div>
          {rows.map((r) => (
            <div
              key={r[0]}
              className={`grid min-w-[580px] grid-cols-12 gap-1 border-b ${sim.border} px-2 py-1 ${sim.rowHover}`}
            >
              <span className="col-span-2 font-mono font-semibold text-zinc-900">{r[0]}</span>
              <span className="col-span-3 truncate">{r[1]}</span>
              <span className="col-span-2">{r[2]}</span>
              <span className="col-span-2">
                <span className={`rounded border ${sim.accentBorder} ${sim.accentSoft} px-1.5 py-0.5 font-semibold text-[#7B6EE8] dark:text-violet-200`}>
                  {r[3]}
                </span>
              </span>
              <span className="col-span-2 text-zinc-500">{r[4]}</span>
              <span className="col-span-1 text-zinc-600">{r[5]}</span>
            </div>
          ))}
        </div>
        <DataTablePagination from={1} to={4} total={17} pageSize={10} />
      </TableShell>
    </div>
  );
}

function PanelApplications() {
  const rows = [
    ['APP-8891', 'Priya S.', 'Staff Eng · Acme', 'Interview', '87', 'Apr 9'],
    ['APP-8888', 'Amit K.', 'Staff Eng · Acme', 'Screen', '81', 'Apr 8'],
    ['APP-8873', 'Rahul M.', 'PM · Northwind', 'New', '76', 'Apr 7'],
    ['APP-8860', 'Neha R.', 'Designer · Globex', 'Offer', '90', 'Apr 6'],
    ['APP-8852', 'Vikram S.', 'SRE · Contoso', 'Hired', '88', 'Apr 5'],
  ];
  return (
    <div className="space-y-1.5">
      <PageHeader
        title="Applications"
        subtitle="List · stage rules · Role-fit on every row · email & notes"
      />
      <TableShell>
        <DataTableToolbar
          left={
            <>
              <TbBtn>
                <Filter className="h-3 w-3" /> Stage
              </TbBtn>
              <TbBtn>
                <SlidersHorizontal className="h-3 w-3" /> View
              </TbBtn>
              <TbBtn>Kanban</TbBtn>
            </>
          }
          right={
            <>
              <TbBtn>
                <Download className="h-3 w-3" /> Export
              </TbBtn>
              <TbBtn>Bulk email</TbBtn>
            </>
          }
        />
        <div className="overflow-x-auto text-[9px]">
          <div
            className={`grid min-w-[560px] grid-cols-[28px_1fr_1.3fr_1fr_0.9fr_0.7fr_0.9fr] gap-1 border-b ${sim.border} ${sim.chrome} px-2 py-1 font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400`}
          >
            <span className="flex justify-center">☐</span>
            <span>ID</span>
            <span>Candidate</span>
            <span>Job</span>
            <span>Stage</span>
            <span>Fit</span>
            <span>Updated</span>
          </div>
          {rows.map((r) => (
            <div
              key={r[0]}
              className={`grid min-w-[560px] grid-cols-[28px_1fr_1.3fr_1fr_0.9fr_0.7fr_0.9fr] gap-1 border-b ${sim.border} px-2 py-1 ${sim.rowHover}`}
            >
              <span className="flex justify-center text-zinc-400">☐</span>
              <span className="font-mono text-[#000000] dark:text-zinc-100">{r[0]}</span>
              <span className="truncate font-medium text-zinc-900">{r[1]}</span>
              <span className="truncate text-zinc-600">{r[2]}</span>
              <span className="text-zinc-700">{r[3]}</span>
              <span className="font-bold text-[#8E86FF] dark:text-violet-300">{r[4]}</span>
              <span className="text-zinc-500">{r[5]}</span>
            </div>
          ))}
        </div>
        <DataTablePagination from={1} to={5} total={29} pageSize={10} />
      </TableShell>
    </div>
  );
}

function PanelInterviews() {
  const rows = [
    ['Wed 16', '10:00', 'Priya S.', 'Staff Eng · panel', 'Google Meet', 'Confirmed'],
    ['Wed 16', '14:30', 'Rahul M.', 'PM · HM', 'Teams', 'Tentative'],
    ['Thu 17', '11:15', 'Amit K.', 'Staff Eng · tech', 'Zoom', 'Confirmed'],
    ['Fri 18', '09:30', 'Neha R.', 'Design · portfolio', 'Meet', 'Confirmed'],
  ];
  return (
    <div className="space-y-1.5">
      <PageHeader title="Interviews" subtitle="Schedule · calendar sync · prep packs & feedback" />
      <TableShell>
        <DataTableToolbar
          left={
            <>
              <TbBtn>
                <Filter className="h-3 w-3" /> Week
              </TbBtn>
              <div className="flex flex-wrap gap-0.5">
                {['Mon 14', 'Tue 15', 'Wed 16', 'Thu 17', 'Fri 18'].map((d, i) => (
                  <span
                    key={d}
                    className={`rounded px-1.5 py-0.5 text-[9px] font-medium ${
                      i === 2 ? `${sim.accentBg} text-white` : `border ${sim.border} ${sim.chrome} text-zinc-600`
                    }`}
                  >
                    {d}
                  </span>
                ))}
              </div>
            </>
          }
          right={
            <TbBtn>
              <Plus className="h-3 w-3" /> Schedule
            </TbBtn>
          }
        />
        <div className="overflow-x-auto text-[9px]">
          <div
            className={`grid min-w-[560px] grid-cols-12 gap-1 border-b ${sim.border} ${sim.chrome} px-2 py-1 font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400`}
          >
            <span className="col-span-2">Day</span>
            <span className="col-span-2">Time</span>
            <span className="col-span-2">Candidate</span>
            <span className="col-span-3">Session</span>
            <span className="col-span-2">Location</span>
            <span className="col-span-1">State</span>
          </div>
          {rows.map((r) => (
            <div
              key={r.join('-')}
              className={`grid min-w-[560px] grid-cols-12 gap-1 border-b ${sim.border} px-2 py-1 ${sim.rowHover}`}
            >
              <span className="col-span-2 text-zinc-500">{r[0]}</span>
              <span className="col-span-2 font-semibold text-[#8E86FF] dark:text-violet-300">{r[1]}</span>
              <span className="col-span-2">{r[2]}</span>
              <span className="col-span-3 truncate text-[#000000] dark:text-zinc-100">{r[3]}</span>
              <span className="col-span-2 text-zinc-600">{r[4]}</span>
              <span className="col-span-1 text-emerald-600">{r[5]}</span>
            </div>
          ))}
        </div>
        <DataTablePagination from={1} to={4} total={14} pageSize={10} />
      </TableShell>
    </div>
  );
}

function PanelBestCandidates() {
  const rows = [
    ['1', 'Priya S.', '92', 'Java · leadership', 'REQ-2044', 'Shortlist'],
    ['2', 'Amit K.', '88', 'Architecture', 'REQ-2044', 'Shortlist'],
    ['3', 'Neha R.', '84', 'Stakeholder', 'REQ-2044', 'Review'],
    ['4', 'Kiran Rao', '82', 'Cloud', 'REQ-2044', 'Review'],
    ['5', 'Vikram S.', '79', 'SRE', 'REQ-2044', 'Pool'],
  ];
  return (
    <div className="space-y-1.5">
      <PageHeader
        title="Best candidates"
        subtitle="Ranked shortlist for Staff Engineer · REQ-2044 · bulk actions"
      />
      <TableShell>
        <DataTableToolbar
          left={
            <>
              <TbBtn>
                <Filter className="h-3 w-3" /> Min score
              </TbBtn>
              <TbBtn>
                <RefreshCw className="h-3 w-3" /> Recompute
              </TbBtn>
            </>
          }
          right={
            <>
              <TbBtn>
                <Download className="h-3 w-3" /> Export
              </TbBtn>
              <TbBtn>Add to pipeline</TbBtn>
            </>
          }
        />
        <div className="overflow-x-auto text-[9px]">
          <div
            className={`grid min-w-[560px] grid-cols-[28px_0.5fr_1fr_0.9fr_1.2fr_1fr_0.9fr] gap-1 border-b ${sim.border} ${sim.chrome} px-2 py-1 font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400`}
          >
            <span className="flex justify-center">☐</span>
            <span>#</span>
            <span>Name</span>
            <span>Score</span>
            <span>Highlights</span>
            <span>Job</span>
            <span>Stage</span>
          </div>
          {rows.map((r) => (
            <div
              key={r[1]}
              className={`grid min-w-[560px] grid-cols-[28px_0.5fr_1fr_0.9fr_1.2fr_1fr_0.9fr] gap-1 border-b ${sim.border} px-2 py-1 ${sim.rowHover}`}
            >
              <span className="flex justify-center text-zinc-400">☐</span>
              <span className="font-bold text-[#8E86FF] dark:text-violet-300">{r[0]}</span>
              <span className="font-medium text-zinc-900">{r[1]}</span>
              <span className="font-black text-[#7B6EE8] dark:text-violet-200">{r[2]}</span>
              <span className="truncate text-zinc-600">{r[3]}</span>
              <span className="truncate font-mono text-[#000000] dark:text-zinc-100">{r[4]}</span>
              <span className="text-zinc-500">{r[5]}</span>
            </div>
          ))}
        </div>
        <DataTablePagination from={1} to={5} total={48} pageSize={10} />
      </TableShell>
    </div>
  );
}

function PanelWorkflows() {
  const rows = [
    ['WF-101', 'Stage → Interview', 'Send prep pack + calendar hold', 'On', '124 runs'],
    ['WF-088', 'Field → Offer amount', 'Notify finance + HM', 'On', '18 runs'],
    ['WF-076', 'New → Screen SLA', 'SLA alert to recruiter', 'Paused', '—'],
    ['WF-064', 'Reject → nurture', 'Add to talent pool email', 'On', '56 runs'],
  ];
  return (
    <div className="space-y-1.5">
      <PageHeader
        title="Workflows"
        subtitle="Triggers on stage / field change · templates for email, tasks, webhooks"
      />
      <TableShell>
        <DataTableToolbar
          left={
            <>
              <TbBtn>
                <Filter className="h-3 w-3" /> Status
              </TbBtn>
              <TbBtn>
                <Plus className="h-3 w-3" /> New workflow
              </TbBtn>
            </>
          }
          right={<TbBtn>View log</TbBtn>}
        />
        <div className="overflow-x-auto text-[9px]">
          <div
            className={`grid min-w-[560px] grid-cols-12 gap-1 border-b ${sim.border} ${sim.chrome} px-2 py-1 font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400`}
          >
            <span className="col-span-2">ID</span>
            <span className="col-span-3">Trigger</span>
            <span className="col-span-4">Action</span>
            <span className="col-span-2">State</span>
            <span className="col-span-1 text-right">Volume</span>
          </div>
          {rows.map((r) => (
            <div
              key={r[0]}
              className={`grid min-w-[560px] grid-cols-12 gap-1 border-b ${sim.border} px-2 py-1 ${sim.rowHover}`}
            >
              <span className="col-span-2 font-mono font-semibold text-zinc-900">{r[0]}</span>
              <span className="col-span-3">{r[1]}</span>
              <span className="col-span-4 text-zinc-600">{r[2]}</span>
              <span className="col-span-2 text-zinc-700">{r[3]}</span>
              <span className="col-span-1 text-right text-zinc-500">{r[4]}</span>
            </div>
          ))}
        </div>
        <DataTablePagination from={1} to={4} total={22} pageSize={10} />
      </TableShell>
    </div>
  );
}

function PanelScheduler() {
  const rows = [
    ['LINK-44a', 'Staff Eng · Acme', 'Round-robin · Neha', 'Apr 9–Apr 18', 'Active'],
    ['LINK-31b', 'PM screen · Northwind', 'Fixed slots', 'Apr 10–Apr 12', 'Active'],
    ['LINK-22c', 'Design · Globex', 'HM + candidate', 'Apr 5–Apr 14', 'Paused'],
  ];
  return (
    <div className="space-y-1.5">
      <PageHeader title="Scheduler" subtitle="Self-serve booking links · availability rules · panel sync" />
      <TableShell>
        <DataTableToolbar
          left={
            <>
              <TbBtn>
                <Filter className="h-3 w-3" /> Job
              </TbBtn>
              <TbBtn>
                <Plus className="h-3 w-3" /> New link
              </TbBtn>
            </>
          }
          right={
            <div className="flex flex-wrap gap-0.5">
              {['09:00', '10:30', '14:00', '16:00'].map((slot) => (
                <span
                  key={slot}
                  className={`rounded border ${sim.accentBorder} ${sim.accentSoft} px-1.5 py-0.5 text-[9px] font-semibold text-[#7B6EE8] dark:text-violet-200`}
                >
                  {slot}
                </span>
              ))}
            </div>
          }
        />
        <div className="overflow-x-auto text-[9px]">
          <div
            className={`grid min-w-[560px] grid-cols-12 gap-1 border-b ${sim.border} ${sim.chrome} px-2 py-1 font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400`}
          >
            <span className="col-span-2">Link</span>
            <span className="col-span-3">Role</span>
            <span className="col-span-3">Routing</span>
            <span className="col-span-2">Window</span>
            <span className="col-span-2">Status</span>
          </div>
          {rows.map((r) => (
            <div
              key={r[0]}
              className={`grid min-w-[560px] grid-cols-12 gap-1 border-b ${sim.border} px-2 py-1 ${sim.rowHover}`}
            >
              <span className="col-span-2 font-mono text-zinc-900">{r[0]}</span>
              <span className="col-span-3 truncate">{r[1]}</span>
              <span className="col-span-3 truncate text-zinc-600">{r[2]}</span>
              <span className="col-span-2 text-zinc-500">{r[3]}</span>
              <span className="col-span-2 text-emerald-600">{r[4]}</span>
            </div>
          ))}
        </div>
        <DataTablePagination from={1} to={3} total={9} pageSize={10} />
      </TableShell>
    </div>
  );
}

function PanelCalendarNotes() {
  const rows = [
    ['Apr 9', '09:00', 'Follow-up: Priya — cloud gap', 'APP-8891', 'Priya S.'],
    ['Apr 9', '16:00', 'HM debrief REQ-2044', 'REQ-2044', 'Acme'],
    ['Apr 10', '11:00', 'Send offer pack', 'APP-8860', 'Globex'],
    ['Apr 10', '15:30', 'Agent notes export', 'AI-INT-12', '—'],
  ];
  return (
    <div className="space-y-1.5">
      <PageHeader title="Calendar & notes" subtitle="Shared visibility · tied to applications & reqs" />
      <TableShell>
        <DataTableToolbar
          left={
            <>
              <TbBtn>
                <Filter className="h-3 w-3" /> Range
              </TbBtn>
              <TbBtn>
                <Plus className="h-3 w-3" /> New note
              </TbBtn>
            </>
          }
          right={<TbBtn>Subscribe (ICS)</TbBtn>}
        />
        <div className="overflow-x-auto text-[9px]">
          <div
            className={`grid min-w-[560px] grid-cols-12 gap-1 border-b ${sim.border} ${sim.chrome} px-2 py-1 font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400`}
          >
            <span className="col-span-2">Date</span>
            <span className="col-span-2">Time</span>
            <span className="col-span-4">Note</span>
            <span className="col-span-2">Ref</span>
            <span className="col-span-2">Owner</span>
          </div>
          {rows.map((r) => (
            <div
              key={r[2]}
              className={`grid min-w-[560px] grid-cols-12 gap-1 border-b ${sim.border} px-2 py-1 ${sim.rowHover}`}
            >
              <span className="col-span-2 text-zinc-500">{r[0]}</span>
              <span className="col-span-2 font-medium text-[#8E86FF] dark:text-violet-300">{r[1]}</span>
              <span className="col-span-4 text-[#000000] dark:text-zinc-100">{r[2]}</span>
              <span className="col-span-2 font-mono text-zinc-600">{r[3]}</span>
              <span className="col-span-2 text-zinc-600">{r[4]}</span>
            </div>
          ))}
        </div>
        <DataTablePagination from={1} to={4} total={31} pageSize={10} />
      </TableShell>
    </div>
  );
}

function PanelAiInterviewsFull() {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-zinc-900 dark:text-zinc-50">AI interviews</h3>
        <span className="rounded bg-emerald-500 px-2 py-0.5 text-[9px] font-bold uppercase text-white">Live</span>
      </div>
      <p className="text-[11px] text-zinc-600 dark:text-zinc-300">
        Structured, multilingual sessions with rubric-based scoring — pairs with <strong>Agent</strong> for agentic follow-ups
        and next-best questions.
      </p>
      <div className="rounded-xl border border-[rgba(142,134,255,0.35)] bg-gradient-to-b from-[#f0eeff] to-white p-3 dark:border-violet-800 dark:from-violet-950/40 dark:to-zinc-950">
        <div className="mb-2 flex items-center gap-2">
          <Radio className="h-4 w-4 text-emerald-500" />
          <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400">Session in progress</span>
          <span className="ml-auto text-[9px] text-zinc-500 dark:text-zinc-400">EN · हि · Español</span>
        </div>
        <div className="rounded-lg border border-[#e5e5e5] bg-white px-2 py-1.5 text-[10px] text-[#000000] dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100">
          Agent suggests probe: “Quantify impact of the migration on uptime.”
        </div>
      </div>
    </div>
  );
}

function PanelAgentHub() {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#8E86FF] dark:bg-violet-500">
          <Wand2 className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-zinc-900 dark:text-zinc-50">Agent · Agentic interview</h3>
          <p className="text-[10px] text-zinc-500 dark:text-zinc-400">
            Orchestrates probes, follow-ups, and scoring hooks across AI interviews
          </p>
        </div>
      </div>
      <div className="grid gap-2">
        {[
          { t: 'Context pack', d: 'JD + prior answers + role-fit gaps' },
          { t: 'Next question', d: 'Generated from rubric & fairness rules' },
          { t: 'Handoff', d: 'Recruiter inbox + pipeline stage update' },
        ].map((x) => (
          <div key={x.t} className="flex justify-between gap-2 rounded-xl border border-zinc-200 bg-white p-2.5 dark:border-zinc-700 dark:bg-zinc-900/80">
            <span className="text-[11px] font-semibold text-[#000000] dark:text-zinc-100">{x.t}</span>
            <span className="text-right text-[10px] text-zinc-500 dark:text-zinc-400">{x.d}</span>
          </div>
        ))}
      </div>
      <p className="flex items-center gap-1 text-[10px] text-[#8E86FF] dark:text-violet-300">
        <Sparkles className="h-3.5 w-3.5" /> Same Agent entry point as in the live top bar
      </p>
    </div>
  );
}

function PanelOrchestratorFull() {
  return (
    <div className="relative min-h-[180px] rounded-xl border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-900/50">
      <div
        className="pointer-events-none absolute inset-0 rounded-xl opacity-35 dark:opacity-25"
        style={{
          backgroundImage: 'radial-gradient(circle, #a78bfa 1px, transparent 1px)',
          backgroundSize: '12px 12px',
        }}
        aria-hidden
      />
      <p className="relative mb-3 text-[11px] font-bold text-zinc-900 dark:text-zinc-50">Process Orchestrator</p>
      <div className="relative flex flex-wrap items-center justify-center gap-2">
        <span className="rounded-lg border border-zinc-200 bg-white px-2 py-1 text-[9px] font-semibold shadow-sm dark:border-zinc-600 dark:bg-zinc-950 dark:text-zinc-200">
          Trigger
        </span>
        <span className="text-[#8E86FF]/70 dark:text-violet-500">→</span>
        <span className="rounded-lg bg-[#8E86FF] px-2 py-1 text-[9px] font-bold text-white dark:bg-violet-500">Orchestrator</span>
        <span className="text-[#8E86FF]/70 dark:text-violet-500">→</span>
        <span className="rounded-lg border border-zinc-200 bg-white px-2 py-1 text-[9px] font-medium text-zinc-700 dark:border-zinc-600 dark:bg-zinc-950 dark:text-zinc-300">
          Workflows · email · tasks
        </span>
      </div>
    </div>
  );
}

function PanelRbacFull() {
  return (
    <div className="divide-y divide-zinc-100 dark:divide-zinc-800 rounded-xl border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-950">
      {[
        ['Org Admin', 'Billing · users · all modules'],
        ['Recruiter', 'Candidates · sequences · clients'],
        ['Hiring Mgr', 'Assigned reqs · feedback'],
      ].map(([role, scope]) => (
        <div key={role} className="flex justify-between gap-2 px-3 py-2 text-[10px]">
          <span className="font-bold text-zinc-900 dark:text-zinc-50">{role}</span>
          <span className="text-right text-zinc-500 dark:text-zinc-400">{scope}</span>
        </div>
      ))}
      <p className="bg-zinc-50 px-3 py-2 text-[9px] text-zinc-500 dark:bg-zinc-900/80 dark:text-zinc-400">
        Keycloak · Manage rights · audit
      </p>
    </div>
  );
}

function PanelReportsFull() {
  /* ── Donut chart helpers ──────────────────────────────────────────────── */
  const donutSlices = [
    { label: 'Hired', pct: 12, color: '#22c55e' },
    { label: 'Offer', pct: 18, color: '#8E86FF' },
    { label: 'Interview', pct: 24, color: '#a78bfa' },
    { label: 'Screened', pct: 28, color: '#c4b5fd' },
    { label: 'Applied', pct: 18, color: '#e0e7ff' },
  ];
  const R = 28; const CX = 36; const CY = 36; const CIRCUM = 2 * Math.PI * R;
  let cumulativePct = 0;
  const donutPaths = donutSlices.map((s) => {
    const offset = CIRCUM * (1 - cumulativePct / 100);
    const dash = CIRCUM * (s.pct / 100);
    cumulativePct += s.pct;
    return { ...s, strokeDasharray: `${dash} ${CIRCUM - dash}`, strokeDashoffset: offset };
  });

  /* ── Time-to-hire line ───────────────────────────────────────────────── */
  const tthPts = [38, 34, 31, 28, 26, 24, 21];
  const tthPath = tthPts.map((v, i) => {
    const x = (i / (tthPts.length - 1)) * 100;
    const y = 100 - ((v - 18) / (42 - 18)) * 100;
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  /* ── Source mix bars ─────────────────────────────────────────────────── */
  const sources = [
    { src: 'LinkedIn', pct: 38, color: '#8E86FF' },
    { src: 'Referral', pct: 27, color: '#a78bfa' },
    { src: 'Career site', pct: 18, color: '#c4b5fd' },
    { src: 'Agency', pct: 11, color: '#ddd6fe' },
    { src: 'Other', pct: 6, color: '#ede9fe' },
  ];

  return (
    <div className="space-y-2">
      <PageHeader title="Reports & Analytics" subtitle="Pipeline health, hiring velocity, source ROI — last 90 days" />

      {/* KPI row */}
      <div className="grid grid-cols-4 gap-1.5">
        {[
          { k: 'Time-to-hire', v: '21d', delta: '−17d', up: true },
          { k: 'Offer accept rate', v: '84%', delta: '+6%', up: true },
          { k: 'Pipeline velocity', v: '4.2×', delta: '+0.8×', up: true },
          { k: 'Cost per hire', v: '₹32K', delta: '−₹8K', up: true },
        ].map((m) => (
          <div key={m.k} className={`rounded-lg border ${sim.border} ${sim.surface} px-2 py-1.5 shadow-sm`}>
            <p className={`text-[7px] font-semibold uppercase tracking-wide ${sim.muted}`}>{m.k}</p>
            <p className={`text-sm font-black leading-tight ${sim.page}`}>{m.v}</p>
            <p className="text-[8px] font-semibold text-emerald-600 dark:text-emerald-400">{m.delta}</p>
          </div>
        ))}
      </div>

      {/* Row 2: Donut + Funnel */}
      <div className="grid grid-cols-2 gap-1.5">
        {/* Pipeline stage donut */}
        <div className={`rounded-lg border ${sim.border} ${sim.surface} p-2 shadow-sm`}>
          <p className="mb-1.5 text-[9px] font-semibold text-zinc-800 dark:text-zinc-100">Pipeline by stage</p>
          <div className="flex items-center gap-2">
            <svg width="72" height="72" viewBox="0 0 72 72" className="shrink-0">
              <circle cx={CX} cy={CY} r={R} fill="none" stroke="#f3f4f6" strokeWidth="10" className="dark:stroke-zinc-800" />
              {donutPaths.map((s) => (
                <motion.circle
                  key={s.label}
                  cx={CX} cy={CY} r={R}
                  fill="none"
                  stroke={s.color}
                  strokeWidth="10"
                  strokeDasharray={s.strokeDasharray}
                  strokeDashoffset={s.strokeDashoffset}
                  strokeLinecap="butt"
                  transform={`rotate(-90 ${CX} ${CY})`}
                  initial={{ strokeDasharray: `0 ${CIRCUM}` }}
                  animate={{ strokeDasharray: s.strokeDasharray }}
                  transition={{ duration: 0.7, delay: donutSlices.indexOf(s) * 0.1 }}
                />
              ))}
              <text x={CX} y={CY - 3} textAnchor="middle" className="fill-zinc-900 dark:fill-zinc-50" fontSize="8" fontWeight="800">84%</text>
              <text x={CX} y={CY + 6} textAnchor="middle" className="fill-zinc-400" fontSize="6">fill rate</text>
            </svg>
            <div className="flex-1 space-y-0.5">
              {donutSlices.map((s) => (
                <div key={s.label} className="flex items-center gap-1 text-[8px]">
                  <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: s.color }} />
                  <span className="flex-1 text-zinc-600 dark:text-zinc-300">{s.label}</span>
                  <span className="font-bold text-zinc-800 dark:text-zinc-100">{s.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Time-to-hire trend */}
        <div className={`rounded-lg border ${sim.border} ${sim.surface} p-2 shadow-sm`}>
          <div className="mb-1 flex items-center justify-between">
            <p className="text-[9px] font-semibold text-zinc-800 dark:text-zinc-100">Time-to-hire trend</p>
            <span className="text-[8px] font-bold text-emerald-600">↓ 45%</span>
          </div>
          <div className="relative h-20 w-full">
            <svg viewBox="0 0 100 100" className="h-full w-full overflow-visible" preserveAspectRatio="none">
              <defs>
                <linearGradient id="tthFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8E86FF" stopOpacity="0.22" />
                  <stop offset="100%" stopColor="#8E86FF" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={`${tthPath} L 100 100 L 0 100 Z`} fill="url(#tthFill)" vectorEffect="non-scaling-stroke" />
              <path d={tthPath} fill="none" stroke="#8E86FF" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinecap="round" />
              {tthPts.map((v, i) => (
                <circle key={i} cx={(i / (tthPts.length - 1)) * 100} cy={100 - ((v - 18) / (42 - 18)) * 100} r="2.5" fill="#8E86FF" vectorEffect="non-scaling-stroke" />
              ))}
            </svg>
            <div className="mt-0.5 flex justify-between text-[7px] text-zinc-400 px-0.5">
              {['W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7'].map((w) => <span key={w}>{w}</span>)}
            </div>
          </div>
          <p className="mt-0.5 text-[8px] text-zinc-400">Avg this period: <span className="font-bold text-zinc-700 dark:text-zinc-200">21 days</span></p>
        </div>
      </div>

      {/* Row 3: Source mix horizontal bars + Monthly hires bar chart */}
      <div className="grid grid-cols-2 gap-1.5">
        {/* Source mix */}
        <div className={`rounded-lg border ${sim.border} ${sim.surface} p-2 shadow-sm`}>
          <p className="mb-1.5 text-[9px] font-semibold text-zinc-800 dark:text-zinc-100">Source mix</p>
          <div className="space-y-1">
            {sources.map((s) => (
              <div key={s.src} className="space-y-0.5">
                <div className="flex justify-between text-[8px]">
                  <span className="text-zinc-600 dark:text-zinc-300">{s.src}</span>
                  <span className="font-bold text-zinc-800 dark:text-zinc-100">{s.pct}%</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: s.color }}
                    initial={{ width: 0 }}
                    animate={{ width: `${s.pct}%` }}
                    transition={{ duration: 0.6, delay: sources.indexOf(s) * 0.08 }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly hires grouped bar */}
        <div className={`rounded-lg border ${sim.border} ${sim.surface} p-2 shadow-sm`}>
          <p className="mb-1 text-[9px] font-semibold text-zinc-800 dark:text-zinc-100">Monthly hires vs targets</p>
          <div className="flex h-[72px] items-end gap-1.5 px-0.5">
            {[
              { m: 'Jan', actual: 55, target: 60 },
              { m: 'Feb', actual: 68, target: 65 },
              { m: 'Mar', actual: 72, target: 70 },
              { m: 'Apr', actual: 85, target: 75 },
            ].map((b) => (
              <div key={b.m} className="flex flex-1 flex-col items-center gap-0.5">
                <div className="flex w-full items-end justify-center gap-0.5">
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${b.actual}%` }}
                    className="w-[45%] rounded-t bg-[#8E86FF] dark:bg-violet-500"
                    style={{ minHeight: 4 }}
                  />
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${b.target}%` }}
                    className="w-[45%] rounded-t bg-zinc-300 dark:bg-zinc-600"
                    style={{ minHeight: 4 }}
                  />
                </div>
                <span className="text-[7px] text-zinc-400">{b.m}</span>
              </div>
            ))}
          </div>
          <div className="mt-1 flex items-center gap-2 text-[7px] text-zinc-500">
            <span className="flex items-center gap-0.5"><span className="inline-block h-2 w-2 rounded-sm bg-[#8E86FF]" /> Actual</span>
            <span className="flex items-center gap-0.5"><span className="inline-block h-2 w-2 rounded-sm bg-zinc-300 dark:bg-zinc-600" /> Target</span>
          </div>
        </div>
      </div>

      {/* Quick links */}
      <div className="flex flex-wrap gap-1">
        {['Export PDF', 'Schedule email', 'Funnel drill-down', 'Custom report'].map((a) => (
          <button key={a} type="button" className={`rounded-lg px-2 py-1 text-[9px] font-semibold shadow-sm border ${sim.border} ${sim.surface} text-zinc-700 dark:text-zinc-200`}>{a}</button>
        ))}
      </div>
    </div>
  );
}

function MainPanel({ viewId, drillId }: { viewId: ViewId; drillId?: string | null }) {
  if (drillId === 'candidateDetail') return <PanelCandidateDetail />;
  if (drillId === 'jobDetail') return <PanelJobDetail />;
  switch (viewId) {
    case 'dashboard':
      return <PanelDashboardFull />;
    case 'candidates':
      return <PanelCandidatesFull />;
    case 'clients':
      return <PanelClients />;
    case 'jobs':
      return <PanelJobs />;
    case 'requisitions':
      return <PanelRequisitions />;
    case 'applications':
      return <PanelApplications />;
    case 'interviews':
      return <PanelInterviews />;
    case 'aiInterviews':
      return <PanelAiInterviewsFull />;
    case 'precisionMatch':
      return <PanelPrecisionMatch />;
    case 'bestCandidates':
      return <PanelBestCandidates />;
    case 'agentHub':
      return <PanelAgentHub />;
    case 'workflows':
      return <PanelWorkflows />;
    case 'orchestrator':
      return <PanelOrchestratorFull />;
    case 'scheduler':
      return <PanelScheduler />;
    case 'calendarNotes':
      return <PanelCalendarNotes />;
    case 'reports':
      return <PanelReportsFull />;
    case 'rbac':
      return <PanelRbacFull />;
    default:
      return null;
  }
}

/**
 * Animated fake mouse cursor — sits in a `pointer-events-none absolute inset-0 z-50` layer.
 * Uses framer-motion to smoothly interpolate between (x%, y%) positions.
 * A ripple ring expands + fades when `clicking` is true.
 */
function CursorOverlay({ x, y, clicking }: { x: number; y: number; clicking: boolean }) {
  return (
    <div className="pointer-events-none absolute inset-0 z-50 overflow-hidden">
      <motion.div
        className="absolute"
        animate={{ left: `${x}%`, top: `${y}%` }}
        transition={{ duration: 0.85, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{ translateX: '-3px', translateY: '-2px' }}
      >
        {/* Click ripple rings */}
        <AnimatePresence>
          {clicking && (
            <>
              <motion.span
                key="ripple-outer"
                initial={{ scale: 0.2, opacity: 0.55 }}
                animate={{ scale: 2.8, opacity: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="absolute -left-4 -top-4 h-8 w-8 rounded-full bg-[#8E86FF]/40"
              />
              <motion.span
                key="ripple-inner"
                initial={{ scale: 0.2, opacity: 0.7 }}
                animate={{ scale: 1.6, opacity: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="absolute -left-3 -top-3 h-6 w-6 rounded-full bg-[#8E86FF]/60"
              />
            </>
          )}
        </AnimatePresence>
        {/* Cursor shape */}
        <svg
          width="18"
          height="21"
          viewBox="0 0 18 21"
          fill="none"
          className="drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]"
          aria-hidden
        >
          <path
            d="M2.5 1.5L15.5 10L10 11.8L7 18.5L2.5 1.5Z"
            fill="white"
            stroke="#1a1a1a"
            strokeWidth="1.4"
            strokeLinejoin="round"
          />
        </svg>
      </motion.div>
    </div>
  );
}

function TopBar({
  viewId,
  navOpen,
  setNavOpen,
}: {
  viewId: ViewId;
  navOpen: boolean;
  setNavOpen: (v: boolean) => void;
}) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setNavOpen(false);
    };
    if (navOpen) document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [navOpen, setNavOpen]);

  return (
    <header className="shrink-0 sticky top-0 z-30 border-b border-[#e5e5e5] bg-white shadow-[0_4px_24px_-8px_rgba(142,134,255,0.14)] dark:border-zinc-700 dark:bg-zinc-950 dark:shadow-[0_4px_24px_-8px_rgba(0,0,0,0.35)]">
      <div className="px-3 sm:px-4 py-2.5 flex flex-col gap-2.5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 justify-between">
          <p className="text-[10px] sm:text-[11px] font-semibold text-zinc-700 dark:text-zinc-200 truncate min-w-0 max-w-full sm:max-w-[min(100%,280px)]">
            {breadcrumbFor(viewId)}
          </p>
          <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap justify-end ml-auto">
            <button
              type="button"
              className="text-[9px] sm:text-[10px] font-semibold text-zinc-600 dark:text-zinc-300 px-2 py-1 rounded-lg hover:bg-[rgba(142,134,255,0.08)] dark:hover:bg-violet-950/50 border border-transparent hover:border-[rgba(142,134,255,0.35)] dark:hover:border-violet-800"
            >
              Recent <ChevronDown className="inline w-3 h-3" />
            </button>
            <div className="relative" ref={ref}>
              <button
                type="button"
                onClick={() => setNavOpen(!navOpen)}
                className={`text-[9px] sm:text-[10px] font-semibold px-2 py-1 rounded-lg flex items-center gap-0.5 border ${
                  navOpen
                    ? 'bg-[rgba(142,134,255,0.12)] text-[#000000] border-[rgba(142,134,255,0.35)] dark:bg-violet-950/60 dark:text-violet-100 dark:border-violet-800'
                    : 'hover:bg-zinc-50 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-200 border-zinc-200 dark:border-zinc-600'
                }`}
              >
                Navigation <ChevronDown className="w-3 h-3" />
              </button>
              <AnimatePresence>
                {navOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="absolute right-0 top-full mt-1 z-50 w-[min(100vw-2rem,320px)] rounded-xl border border-zinc-200 bg-white shadow-xl p-3 grid gap-3 dark:border-zinc-700 dark:bg-zinc-950"
                  >
                    {NAV_DROPDOWN_GROUPS.map((g) => (
                      <div key={g.title}>
                        <p className="text-[9px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wide mb-1">{g.title}</p>
                        <ul className="space-y-0.5">
                          {g.items.map((it) => (
                            <li key={it} className="text-[10px] text-zinc-700 dark:text-zinc-200 py-0.5 px-1 rounded hover:bg-[rgba(142,134,255,0.08)] dark:hover:bg-violet-950/40">
                              {it}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="flex-1 min-w-[160px] flex items-center gap-2 rounded-full border border-[rgba(142,134,255,0.35)] bg-gradient-to-r from-[#f3f3f3] to-[#f0eeff] px-3 py-1.5 shadow-inner dark:border-zinc-600 dark:from-zinc-900 dark:to-zinc-900/80">
            <Search className="w-3.5 h-3.5 text-[#8E86FF] dark:text-violet-400 shrink-0" />
            <span className="text-[10px] sm:text-[11px] text-zinc-500 dark:text-zinc-400 truncate">Search by name, email, job…</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap shrink-0">
            <button type="button" className="px-1 text-[9px] font-semibold text-zinc-600 hover:text-[#7B6EE8] dark:text-zinc-300 dark:hover:text-violet-300 sm:text-[10px]">
              Help
            </button>
            <button
              type="button"
              className="flex items-center gap-1 text-[9px] sm:text-[10px] font-bold text-white bg-[#8E86FF] dark:bg-violet-500 px-3 py-1.5 rounded-full shadow-md shadow-[rgba(142,134,255,0.35)] ring-1 ring-white/10"
            >
              <Bot className="w-3.5 h-3.5" /> Agent
            </button>
            <Bell className="w-4 h-4 text-zinc-500 dark:text-zinc-400 hover:text-[#8E86FF] dark:hover:text-violet-400 cursor-default" />
            <button
              type="button"
              onClick={toggleTheme}
              className="hidden sm:inline-flex p-0.5 rounded-lg text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800"
              aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <div
              className="h-8 w-8 rounded-full bg-gradient-to-br from-[#8E86FF] to-[#7B6EE8] border-2 border-white dark:border-zinc-700 shadow shrink-0"
              title="Account"
            />
            <LogOut className="w-3.5 h-3.5 text-zinc-300 dark:text-zinc-600 hidden sm:block" />
          </div>
        </div>
      </div>
    </header>
  );
}

export type B2BHeroDashboardShowcaseProps = {
  /** Hero column layout: icon rail + capped height — matches B2C hero preview scale */
  compact?: boolean;
  /**
   * When provided, the showcase uses this index as the active view and disables
   * its own auto-cycle timer. Lets a parent component (e.g. FeaturesPage) drive
   * both the cursor animation and the view simultaneously.
   */
  controlledIdx?: number;
  /**
   * Override the default height/min-height/max-height classes.
   * Useful when the parent needs an exact fixed height (e.g. features hero overlay).
   */
  heightClass?: string;
};

export function B2BHeroDashboardShowcase({
  compact = false,
  controlledIdx,
  heightClass,
}: B2BHeroDashboardShowcaseProps) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isLg, setIsLg] = useState(false);
  /** Tour-specific state */
  const [tourStep, setTourStep] = useState(0);
  const [clicking, setClicking] = useState(false);
  const [activeDrillId, setActiveDrillId] = useState<string | null>(null);
  const currentTourStep = TOUR_SCRIPT[tourStep];

  /** Resolved active view index — external control takes precedence */
  const activeIdx = controlledIdx !== undefined ? controlledIdx : idx;
  const active = FLAT_VIEWS[activeIdx];
  const sidebarCollapsedEffective = compact || (sidebarCollapsed && isLg);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const apply = () => {
      setIsLg(mq.matches);
      if (!mq.matches) setSidebarCollapsed(false);
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  /**
   * Tour engine — replaces the simple auto-cycle.
   * Each step:  move cursor (850ms) → click ripple (350ms) → show panel/drill → dwell → advance.
   * Disabled when a parent drives the index via controlledIdx.
   */
  useEffect(() => {
    if (paused || controlledIdx !== undefined) return;

    const step = TOUR_SCRIPT[tourStep];
    const timers: ReturnType<typeof setTimeout>[] = [];
    const add = (fn: () => void, ms: number) => {
      const t = setTimeout(fn, ms);
      timers.push(t);
    };

    // Navigate panel immediately (for non-drill steps clear any leftover drill)
    if (!step.drillId) {
      const vi = FLAT_VIEWS.findIndex((v) => v.id === step.viewId);
      if (vi !== -1) setIdx(vi);
      setActiveDrillId(null);
    }

    // After cursor animation lands, fire click
    add(() => {
      setClicking(true);
      add(() => {
        setClicking(false);
        if (step.drillId) {
          const vi = FLAT_VIEWS.findIndex((v) => v.id === step.viewId);
          if (vi !== -1) setIdx(vi);
          setActiveDrillId(step.drillId);
        }
        // Dwell, then advance to next step
        add(() => {
          setActiveDrillId(null);
          setTourStep((s) => (s + 1) % TOUR_SCRIPT.length);
        }, step.dwellMs);
      }, 350);
    }, 900);

    return () => timers.forEach(clearTimeout);
  }, [tourStep, paused, controlledIdx]);


  const resolvedHeightClass =
    heightClass ??
    (compact
      ? 'h-[min(52vh,380px)] lg:h-[min(48vh,420px)] max-h-[420px] min-h-[240px]'
      : 'min-h-[min(88vh,780px)] lg:min-h-[620px] max-h-[min(92vh,900px)]');

  return (
    <div
      style={{ ['--brand-cta-bg']: '#8E86FF' } as CSSProperties}
      className={`relative w-full overflow-hidden border border-[#e5e5e5] bg-white shadow-xl md:shadow-2xl dark:border-zinc-700 dark:bg-zinc-950 flex flex-col lg:flex-row ${
        compact
          ? 'rounded-xl sm:rounded-2xl md:rounded-[1.75rem]'
          : 'rounded-[1.25rem] sm:rounded-[1.75rem] md:rounded-[2rem] shadow-[0_25px_80px_-12px_rgba(0,0,0,0.28)] dark:shadow-[0_25px_80px_-12px_rgba(0,0,0,0.5)]'
      } ${resolvedHeightClass}`}
      role="region"
      aria-label="Interactive product simulation"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Sidebar — pointer-events-none: tour drives navigation, users cannot click */}
      <aside
        className={`pointer-events-none shrink-0 flex flex-col border-b border-[#e5e5e5] bg-[#f3f3f3] transition-[width] duration-200 ease-out dark:border-zinc-700 dark:bg-zinc-900/95 lg:max-h-none lg:border-b-0 lg:border-r max-h-[42vh] ${
          sidebarCollapsedEffective
            ? 'w-14 min-w-[3.5rem] lg:max-w-[3.5rem]'
            : 'w-full min-w-0 lg:w-[252px] lg:max-w-[252px]'
        }`}
      >
        <div
          className={`flex shrink-0 items-center gap-2 border-b border-[#e5e5e5] bg-white/80 px-2 py-3 backdrop-blur-[2px] dark:border-zinc-700 dark:bg-zinc-950/80 ${
            sidebarCollapsedEffective ? 'justify-between' : ''
          }`}
        >
          <GGJLogoMark size={sidebarCollapsedEffective ? 28 : 32} className="shrink-0" />
          {!sidebarCollapsedEffective && (
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
              <span className="text-sm font-extrabold leading-none tracking-tight text-zinc-900 dark:text-zinc-50">
                GoGrabJob
              </span>
              <span className="inline-flex items-center rounded-md bg-[rgba(142,134,255,0.12)] px-1.5 py-0.5 text-[9px] font-bold text-[#7B6EE8] ring-1 ring-[rgba(142,134,255,0.35)] dark:bg-violet-950/50 dark:text-violet-200 dark:ring-violet-800">
                Business
              </span>
            </div>
          )}
          {!compact && (
            <button
              type="button"
              onClick={() => setSidebarCollapsed((c) => !c)}
              className={`hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#e5e5e5] bg-white text-zinc-600 transition-colors hover:border-[rgba(142,134,255,0.35)] hover:bg-[rgba(142,134,255,0.08)] hover:text-[#7B6EE8] dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:border-violet-800 dark:hover:bg-violet-950/40 lg:flex ${
                sidebarCollapsedEffective ? 'ml-auto' : ''
              }`}
              aria-label={sidebarCollapsedEffective ? 'Expand navigation' : 'Collapse navigation'}
              title={sidebarCollapsedEffective ? 'Expand' : 'Collapse'}
            >
              {sidebarCollapsedEffective ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          )}
        </div>
        <div
          className={`flex shrink-0 items-center gap-2 border-b border-[#e5e5e5] bg-[#f3f3f3]/90 dark:border-zinc-700 dark:bg-zinc-900/90 ${
            sidebarCollapsedEffective ? 'justify-center px-2 py-2' : 'px-3 py-2'
          }`}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-white bg-gradient-to-br from-[#8E86FF] to-[#7B6EE8] text-[10px] font-bold text-white shadow dark:border-zinc-700">
            N
          </div>
          {!sidebarCollapsedEffective && (
            <div className="min-w-0">
              <p className="truncate text-[11px] font-semibold text-zinc-900 dark:text-zinc-50">Neha</p>
              <p className="truncate text-[9px] text-zinc-500 dark:text-zinc-400">Talent · Recruiting</p>
            </div>
          )}
        </div>
        {/* overflow-hidden always: tour drives nav, no user scroll needed */}
        <nav className={`flex-1 space-y-3 overflow-hidden px-1.5 py-2 sm:px-2 min-h-0 ${compact ? 'max-h-[200px] lg:max-h-none' : ''}`}>
          {SECTIONS.map((section) => (
            <div key={section.title}>
              {!sidebarCollapsedEffective && (
                <p className="mb-1 px-2 text-[9px] font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
                  {section.title}
                </p>
              )}
              <div className={`space-y-0.5 ${sidebarCollapsedEffective ? 'space-y-1' : ''}`}>
                {section.items.map((item) => {
                  const i = FLAT_VIEWS.findIndex((v) => v.id === item.id);
                  const isActive = i === activeIdx;
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      title={sidebarCollapsedEffective ? item.label : undefined}
                      onClick={() => setIdx(i)}
                      className={`w-full flex items-center gap-2 rounded-lg text-left transition-colors ${
                        sidebarCollapsedEffective ? 'justify-center px-0 py-2 relative' : 'px-2 py-1.5'
                      } ${
                        isActive
                          ? 'bg-[#8E86FF] text-white shadow-md ring-1 ring-[rgba(142,134,255,0.35)] dark:bg-violet-500'
                          : 'text-zinc-700 hover:bg-white/90 dark:text-zinc-200 dark:hover:bg-zinc-800/90'
                      }`}
                    >
                      <Icon className={`w-3.5 h-3.5 shrink-0 ${isActive ? 'text-white' : 'text-[#8E86FF] dark:text-violet-400'}`} />
                      {!sidebarCollapsedEffective && (
                        <>
                          <span className="text-[10px] font-semibold leading-tight flex-1">{item.label}</span>
                          {item.badge && (
                            <span
                              className={`text-[8px] font-bold uppercase px-1 py-0.5 rounded ${
                                isActive ? 'bg-white/20 text-white' : 'bg-emerald-500 text-white'
                              }`}
                            >
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                      {sidebarCollapsedEffective && item.badge && (
                        <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-zinc-900" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        {!sidebarCollapsedEffective && (
          <div className="hidden shrink-0 border-t border-[#e5e5e5] bg-white/90 p-2 dark:border-zinc-700 dark:bg-zinc-950/80 lg:block">
            <p className="mb-1 text-[9px] text-zinc-500 dark:text-zinc-400">Trial workspace</p>
            <div className="h-1 overflow-hidden rounded-full bg-[#e5e5e5] dark:bg-zinc-700">
              <motion.div
                className="h-full bg-[#8E86FF] dark:bg-violet-500 rounded-full"
                animate={{ width: ['55%', '78%', '55%'] }}
                transition={{ duration: 10, repeat: Infinity }}
              />
            </div>
          </div>
        )}
      </aside>

      {/* Main: top bar + content — pointer-events-none: cursor tour drives interaction */}
      <div className="pointer-events-none flex min-h-0 min-w-0 flex-1 flex-col bg-white dark:bg-zinc-950">
        <TopBar viewId={active.id} navOpen={navOpen} setNavOpen={setNavOpen} />

        <div
          className={`flex items-center justify-between gap-2 border-b border-[#e5e5e5] bg-[#f3f3f3]/50 px-3 dark:border-zinc-800 dark:bg-zinc-900/50 ${compact ? 'py-1' : 'py-1.5'}`}
        >
          <span className={`text-zinc-500 dark:text-zinc-400 ${compact ? 'text-[8px]' : 'text-[9px]'}`}>
            {paused && controlledIdx === undefined
              ? 'Paused — hover away to resume'
              : activeDrillId
                ? `Detail view · ${FLAT_VIEWS.find((v) => v.id === currentTourStep?.viewId)?.label ?? ''}`
                : `Demo · ${FLAT_VIEWS.find((v) => v.id === currentTourStep?.viewId)?.label ?? ''}`}
          </span>
          <div className="flex gap-0.5">
            {TOUR_SCRIPT.map((_, i) => (
              <div
                key={i}
                className={`h-1 rounded-full transition-all duration-300 ${i === tourStep ? 'w-3 bg-[#8E86FF] dark:bg-violet-500' : 'w-1 bg-zinc-300 dark:bg-zinc-600'}`}
              />
            ))}
          </div>
        </div>

        {/*
         * Both divs use overflow-hidden always: the showcase is a scripted tour display,
         * never a user-scrollable surface. overflow-y-auto would let the browser route
         * wheel events into the inner scroll container, blocking page scroll.
         */}
        <div className="flex-1 overflow-hidden min-h-0">
          <div className={`h-full overflow-hidden ${compact ? 'p-2' : 'p-3 md:p-4'}`}>
            <AnimatePresence mode="wait">
              <motion.div
                key={`${active.id}-${activeIdx}-${activeDrillId ?? ''}`}
                initial={{ opacity: 0, scale: 0.91 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.07 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                style={{ transformOrigin: 'top center' }}
              >
                <MainPanel viewId={active.id} drillId={activeDrillId} />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {!compact && (
          <div className="flex shrink-0 items-center justify-between gap-2 border-t border-[#e5e5e5] bg-[#f3f3f3] px-3 py-2 text-[9px] text-[#616161] dark:border-zinc-800 dark:bg-zinc-900/80 dark:text-zinc-400">
            <span>Simulation · not connected to your org</span>
            <span className="font-medium text-[#8E86FF] dark:text-violet-400">
              Agent · AI interviews · Orchestrator · RBAC
            </span>
          </div>
        )}
      </div>

      {/* Animated cursor overlay — only shown in self-tour mode (no external controlledIdx) */}
      {controlledIdx === undefined && (
        <CursorOverlay
          x={currentTourStep?.cx ?? 60}
          y={currentTourStep?.cy ?? 55}
          clicking={clicking}
        />
      )}
    </div>
  );
}
